import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FinanceSettings } from '@/shared/models/ContextDataModel/interfaces/entities/finance-settings.ts';
import { UpsertFinanceSettings } from '@/shared/models/ContextDataModel/ContextData.ts';
import { FinanceSettingsHeader } from './FinanceSettingsHeader.tsx';
import { FinanceSettingsLineItems } from './FinanceSettingsLineItems.tsx';
import { FinanceSettingsPaymentTerms } from './FinanceSettingsPaymentTerms.tsx';
import { FinanceSettingsOrganization } from '@/shared/forms/FinanceSettingsForm/domain/groupLineItemsByOrganization.ts';
import { FinanceSettingsFormValues } from '@/shared/forms/FinanceSettingsForm/schema/getFinanceSettingsFormSchema.ts';
import { useOrganizationById } from '@/clients/organization/useOrganization.ts';

type FinanceSettingsOrgProps = {
    organization: FinanceSettingsOrganization & {
        organizationId: number;
        offset: number;
    };
    defaultValues?: FinanceSettings[];
    onChange?: (financeSettings: UpsertFinanceSettings) => void;
};

export function FinanceSettingsOrg({ organization, defaultValues, onChange }: FinanceSettingsOrgProps) {
    const { register } = useFormContext<FinanceSettingsFormValues>();
    const { data: org } = useOrganizationById(organization.organizationId);

    return (
        <article className="flex w-full flex-col gap-4 pb-6 last:pb-0">
            <FinanceSettingsHeader
                label={org.name}
                totalAmountNet={organization.totalAmountNet}
                includedProductCategories={organization.includedProductCategories}
                showRoles={organization.offset !== 0}
            />
            {organization.financeSettings.map((financeSetting, subIndex) => {
                const formIndex = organization.offset + subIndex;

                const defaultPaymentTerms = defaultValues?.find(
                    defaultValue =>
                        defaultValue.debtorOrganizationId === financeSetting.debtorOrganizationId &&
                        defaultValue.creditorOrganizationId === financeSetting.creditorOrganizationId
                );
                return (
                    <React.Fragment key={formIndex}>
                        <FinanceSettingsLineItems lineItems={financeSetting.lineItems} />
                        <FinanceSettingsPaymentTerms
                            organizationId={organization.organizationId}
                            isRevenueIncreasingCase={financeSetting.revenueType === 'REVENUE'}
                            includedProductCategories={organization.includedProductCategories}
                            onChange={onChange}
                            creditorOrganizationIdProps={{
                                ...register(`financeSettings.${formIndex}.creditorOrganizationId`, {
                                    value: financeSetting.creditorOrganizationId,
                                }),
                            }}
                            debtorOrganizationIdProps={{
                                ...register(`financeSettings.${formIndex}.debtorOrganizationId`, {
                                    value: financeSetting.debtorOrganizationId,
                                }),
                            }}
                            paymentTermsIdProps={{
                                ...register(`financeSettings.${formIndex}.paymentTermId`),
                                defaultValue: defaultPaymentTerms?.paymentTermId,
                            }}
                            billingMethodProps={{
                                ...register(`financeSettings.${formIndex}.billingMethod`),
                                defaultValue: defaultPaymentTerms?.billingMethod,
                            }}
                            billingTextProps={{
                                ...register(`financeSettings.${formIndex}.billingText`),
                                defaultValue: defaultPaymentTerms?.billingText ?? null,
                            }}
                        />
                    </React.Fragment>
                );
            })}
        </article>
    );
}
